import React from "react";
import { useSiteContext } from "../context/site/useSiteContext";
import { Helmet } from "react-helmet";
import { Box, BoxProps } from "@shared-components/Box/Box";
import { Link } from "@shared-components/Link/Link";
import { useDictionaryContext } from "../context/dictionary/useDictionaryContext";
import { DICTIONARY_MAP } from "./dictionary-map";
import { linkStyles } from "@modules/Footer/styles";
import { getLocalizedUrl } from "../shared/utils/get-localized-url";

declare global {
  interface Window {
    com_carlsberg_compliance_pack: {
      manageCookies: () => void;
    };
  }
}

interface ManageCookiesButtonProps {
  children: React.ReactNode;
}

const ManageCookiesButton: React.FC<ManageCookiesButtonProps> = ({
  children,
}) => {
  const openManageCookies = () => {
    if (window === undefined) {
      return;
    }

    window.com_carlsberg_compliance_pack.manageCookies();
  };

  return (
    <Box
      as="button"
      onClick={openManageCookies}
      sx={{
        appearance: "none",
        bg: "transparent",
        border: "none",
        cursor: "pointer",
        textTransform: "uppercase",
        textAlign: "left",
        ...linkStyles,
      }}
    >
      {children}
    </Box>
  );
};

const ComplianceScript: React.FC = () => {
  const {
    settings: { globalCompliance },
    currentCulture,
  } = useSiteContext();

  const mappedCurrentCulture = currentCulture.split("-");
  const market = globalCompliance.market || mappedCurrentCulture[1] || "";

  const isCarlsbergHeadlessPublished =
    process.env.GATSBY_CB_HEADLESS_ENV === "published";

  const enableAgeGate =
    isCarlsbergHeadlessPublished && process.env.NODE_ENV === "production";

  return (
    <Helmet>
      <script
        async
        id="cbgCompPack"
        src="https://compliance-pack.carlsberggroup.com/cbgCompPack.js"
        data-gtm-id={globalCompliance.googleTagManagerID}
        data-prj-id={globalCompliance.globalCompliancePackID}
        data-market={market}
        data-culture={mappedCurrentCulture[0]}
        data-verify-age={enableAgeGate && globalCompliance.enableAgeGate}
        {...(globalCompliance.privacy && {
          "data-policy-page": globalCompliance.privacy.url,
        })}
        {...(globalCompliance.cookies && {
          "data-cookies-page": globalCompliance.cookies.url,
        })}
      />
    </Helmet>
  );
};

export type ComplianceFooterProps = BoxProps;

const ComplianceFooter: React.FC<ComplianceFooterProps> = ({
  sx,
  ...props
}) => {
  const {
    settings: { globalCompliance },
    currentCulture,
  } = useSiteContext();

  const { get } = useDictionaryContext();

  return (
    <Box as="ul" {...props} sx={sx}>
      <li>
        {globalCompliance.privacy && (
          <Link to={getLocalizedUrl(globalCompliance.privacy, currentCulture)}>
            {globalCompliance.privacy.name}
          </Link>
        )}
        {globalCompliance.cookies && (
          <Link to={getLocalizedUrl(globalCompliance.cookies, currentCulture)}>
            {globalCompliance.cookies.name}
          </Link>
        )}
        {globalCompliance.termsOfUse && (
          <Link
            to={getLocalizedUrl(globalCompliance.termsOfUse, currentCulture)}
          >
            {globalCompliance.termsOfUse.name}
          </Link>
        )}
        {globalCompliance.acceptableUse && (
          <Link
            to={getLocalizedUrl(globalCompliance.acceptableUse, currentCulture)}
          >
            {globalCompliance.acceptableUse.name}
          </Link>
        )}
        <ManageCookiesButton>
          {get(
            DICTIONARY_MAP.global_compliance
              .global_compliance_manage_cookies_button,
            currentCulture
          )}
        </ManageCookiesButton>
      </li>
    </Box>
  );
};

export { ManageCookiesButton, ComplianceScript, ComplianceFooter };
